import React, { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Button, SpaceBetween } from '@amzn/awsui-components-react';
import { MarkdownContent } from '../MarkdownContent';
import { PolicyAssistantStore, PolicyAssistantPageContext } from './PolicyAssistantStore';
import { agenticSurfaceStore } from './agenticSurfaceStore';
import './PolicyAssistantFab.scss';

interface PolicyAssistantFabProps {
  /** Display name / alias of the current user, passed to the agent context. */
  userName?: string;
  /** Returns the user's current location (read live at send time). */
  getPageContext?: () => PolicyAssistantPageContext;
}

/** Paper-plane send icon — matches the one used in the CLARA compose card. */
const PaperPlaneIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 11.5L21 3l-8.5 18-2-8z" />
    <path d="M3 11.5l9 2" />
  </svg>
);

/**
 * Global Policy Assistant launcher.
 *
 * A floating CLARA sparkle button (bottom-right) that opens a lightweight
 * markdown Q&A window. Hidden while any agentic surface is mounted (see
 * `agenticSurfaceStore`) so it never overlaps the dedicated agentic flows.
 *
 * Streams responses through the shared `policy-assistant` agent — no settings,
 * references, WebSocket, or session-timeout machinery.
 */
export const PolicyAssistantFab: React.FC<PolicyAssistantFabProps> = observer(({ userName, getPageContext }) => {
  // One store for the app session — preserves history across open/close.
  const [store] = useState(() => new PolicyAssistantStore(userName));
  const scrollRef = useRef<HTMLDivElement>(null);

  const lastContent = store.messages[store.messages.length - 1]?.content;
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [store.messages.length, store.isLoading, lastContent]);

  // Suppress entirely on agentic surfaces.
  if (agenticSurfaceStore.isActive) return null;

  const handleSend = (): void => {
    if (store.inputValue.trim() && !store.isLoading) {
      void store.sendMessage(store.inputValue, getPageContext?.());
    }
  };

  if (!store.isOpen) {
    return (
      <div className="policy-assistant-fab__launcher">
        <button
          type="button"
          className="policy-assistant-fab__button"
          aria-label="Open Policy Assistant"
          data-testid="policy-assistant-fab"
          onClick={() => store.setOpen(true)}
        >
          <span className="policy-assistant-fab__spark" aria-hidden="true">✦</span>
        </button>
      </div>
    );
  }

  const visibleMessages = store.messages.filter(m => !m.hidden);
  const sendDisabled = !store.inputValue.trim() || store.isLoading;

  return (
    <div
      className="policy-assistant-window"
      role="dialog"
      aria-label="Policy Assistant"
      data-testid="policy-assistant-window"
    >
      <div className="policy-assistant-window__header">
        <span className="policy-assistant-window__title">
          <span className="policy-assistant-window__avatar" aria-hidden="true">✦</span>
          Policy Assistant
        </span>
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="icon"
            iconName="remove"
            ariaLabel="Clear conversation"
            disabled={store.messages.length === 0 || store.isLoading}
            onClick={() => store.clear()}
          />
          <Button
            variant="icon"
            iconName="close"
            ariaLabel="Close Policy Assistant"
            onClick={() => store.setOpen(false)}
          />
        </SpaceBetween>
      </div>

      <div className="policy-assistant-window__messages" ref={scrollRef} data-testid="policy-assistant-messages">
        {visibleMessages.length === 0 && (
          <div className="policy-assistant-window__empty">
            <div className="policy-assistant-window__empty-avatar" aria-hidden="true">✦</div>
            <div className="policy-assistant-window__empty-title">
              {userName ? `Hi @${userName}, I'm CLARA` : "Hi, I'm CLARA"}
            </div>
            <div className="policy-assistant-window__empty-text">
              Ask me about a policy's requirements, key definitions, or where to find what you need.
            </div>
          </div>
        )}
        {visibleMessages.map(m => {
          // Skip the empty placeholder assistant bubble — the thinking bubble covers it.
          if (m.role !== 'user' && !m.content) return null;
          if (m.role === 'user') {
            return (
              <div key={m.id} className="policy-assistant-msg policy-assistant-msg--user">
                <div className="policy-assistant-msg__bubble">{m.content}</div>
              </div>
            );
          }
          return (
            <div key={m.id} className="policy-assistant-msg policy-assistant-msg--assistant">
              <div className="policy-assistant-msg__avatar" aria-hidden="true">✦</div>
              <div className="policy-assistant-msg__body">
                <span className="policy-assistant-msg__label">CLARA</span>
                <div className="policy-assistant-msg__bubble">
                  <MarkdownContent content={m.content} />
                </div>
              </div>
            </div>
          );
        })}
        {store.isLoading && !lastContent && (
          <div className="policy-assistant-msg policy-assistant-msg--assistant">
            <div className="policy-assistant-msg__avatar" aria-hidden="true">✦</div>
            <div className="policy-assistant-msg__body">
              <span className="policy-assistant-msg__label">CLARA</span>
              <div
                className="policy-assistant-msg__bubble policy-assistant-msg__bubble--thinking"
                data-testid="policy-assistant-thinking"
              >
                <span className="policy-assistant-dots" aria-label="Thinking">
                  <span className="policy-assistant-dots__dot" />
                  <span className="policy-assistant-dots__dot" />
                  <span className="policy-assistant-dots__dot" />
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="policy-assistant-window__input">
        <textarea
          className="policy-assistant-window__textarea"
          value={store.inputValue}
          placeholder="Ask a question..."
          rows={2}
          disabled={store.isLoading}
          aria-label="Message Policy Assistant"
          data-testid="policy-assistant-input"
          onChange={e => store.setInputValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <button
          type="button"
          className="policy-assistant-window__send"
          aria-label="Send message"
          disabled={sendDisabled}
          onClick={handleSend}
        >
          <PaperPlaneIcon />
        </button>
      </div>
    </div>
  );
});
